import { useState } from 'react'
import type { JSX, ReactNode } from 'react'

import { Checkbox, LockIcon } from '@buffer-mono/popcorn'

import { ChannelFields } from '~publish/components/Composer/components/ChannelFields'
import {
  CollapsedChannelSection,
  CollapsedImagesPreview,
} from '~publish/components/Composer/components/CollapsedChannelSection'
import { ComposerCardList } from '~publish/components/Composer/components/ComposerCardList'
import { ExpandedComposerLayout } from '~publish/components/Composer/components/ExpandedComposerLayout'
import { StickerChipsField } from '~publish/components/Composer/components/RemindersWithStickers/StickerChipsField'
import { PostTypeBarView } from '~publish/components/Composer/components/PostTypeBar'
import { YoutubeFieldsView } from '~publish/components/Composer/components/YoutubeFieldsView'
import {
  GUEST_NETWORKS,
  getGuestNetworkService,
} from '~publish/helpers/guestNetworks'
import type {
  GuestNetwork,
  GuestNetworkId,
} from '~publish/helpers/guestNetworks'
import { getYoutubeTitleFromText } from '~publish/legacy/composer/composer/components/youtube/utils'
import { getCollapsedChannelIssues } from '~publish/legacy/composer/composer/components/getCollapsedChannelIssues'
import {
  EditorFeedbackNotices,
  getImageLimitNoticeMessage,
} from '~publish/legacy/composer/composer/components/notices/EditorFeedbackNotices'
import type { Image } from '~publish/legacy/composer/composer/entities/factories'
import { getComposerFeedbackMessages } from '~publish/legacy/composer/composer/stores/utils'

import { getGuestNetworkSettings } from './guestNetworkSettings'
import type { GuestNetworkValidation } from './guestNetworkValidation'

import styles from './PublicComposerCards.module.css'

const EMPTY_TEXT_PREVIEW = 'What would you like to share?'

export function useActiveGuestNetwork(
  networks: readonly GuestNetworkId[],
): [GuestNetworkId | undefined, (network: GuestNetworkId) => void] {
  const [chosenNetwork, setChosenNetwork] = useState<GuestNetworkId>()
  const activeNetwork =
    chosenNetwork !== undefined && networks.includes(chosenNetwork)
      ? chosenNetwork
      : GUEST_NETWORKS.find((network) => networks.includes(network.id))?.id
  return [activeNetwork, setChosenNetwork]
}

type PublicComposerCardsProps = Readonly<{
  networks: readonly GuestNetworkId[]
  activeNetwork: GuestNetworkId | undefined
  onActivateNetwork: (network: GuestNetworkId) => void
  text: string
  images: readonly Image[]
  notices: ReactNode
  editor: ReactNode
  toolbar: ReactNode
  uploadDisabled: boolean
  onUploadFiles: (files: File[]) => void
  onRequestSignIn: (trigger: HTMLElement) => void
  validation: GuestNetworkValidation
  isDirty: boolean
}>

export function PublicComposerCards({
  networks,
  activeNetwork,
  onActivateNetwork,
  text,
  images,
  notices,
  editor,
  toolbar,
  uploadDisabled,
  onUploadFiles,
  onRequestSignIn,
  validation,
  isDirty,
}: PublicComposerCardsProps): JSX.Element {
  const selectedNetworks = GUEST_NETWORKS.filter((network) =>
    networks.includes(network.id),
  )
  const textPreview = text || EMPTY_TEXT_PREVIEW

  const renderExpanded = (network?: GuestNetwork): JSX.Element => (
    <ExpandedComposerLayout
      key={network?.id ?? 'omni'}
      service={network ? getGuestNetworkService(network) : undefined}
      isOmni={network === undefined}
      notices={
        network ? (
          <GuestNetworkNotices
            network={network.id}
            validation={validation}
            isDirty={isDirty}
          />
        ) : undefined
      }
      postTypeBars={
        network ? (
          <GuestPostTypeBar
            network={network.id}
            onRequestSignIn={onRequestSignIn}
          />
        ) : undefined
      }
      uploadDisabled={uploadDisabled}
      onUploadDrop={onUploadFiles}
      onUploadPaste={onUploadFiles}
      editor={editor}
      toolbar={toolbar}
      channelFields={
        network ? (
          <GuestChannelFields
            network={network.id}
            text={text}
            onRequestSignIn={onRequestSignIn}
          />
        ) : undefined
      }
    />
  )

  return (
    <ComposerCardList>
      {notices}
      {selectedNetworks.length === 0
        ? renderExpanded()
        : selectedNetworks.map((network) =>
            network.id === activeNetwork ? (
              renderExpanded(network)
            ) : (
              <CollapsedChannelSection
                key={network.id}
                issues={getGuestNetworkIssues(network, validation, isDirty)}
                onClick={(): void => onActivateNetwork(network.id)}
                aria-label={`${network.name}: ${textPreview}`}
              >
                <CollapsedChannelSection.ChannelIcon
                  service={getGuestNetworkService(network)}
                />
                <CollapsedChannelSection.TextPreview>
                  {textPreview}
                </CollapsedChannelSection.TextPreview>
                {images.length > 0 && (
                  <CollapsedChannelSection.AttachmentPreview>
                    <CollapsedImagesPreview images={images} />
                  </CollapsedChannelSection.AttachmentPreview>
                )}
              </CollapsedChannelSection>
            ),
          )}
    </ComposerCardList>
  )
}

function getGuestNetworkIssues(
  network: GuestNetwork,
  validation: GuestNetworkValidation,
  isDirty: boolean,
): ReturnType<typeof getCollapsedChannelIssues> {
  const draft = validation.drafts.get(network.id)
  if (!draft) return []
  return getCollapsedChannelIssues({
    draftId: draft.id,
    serviceName: draft.service.name,
    whatPreventsSaving: [...validation.whatPreventsSaving],
    visibleNotifications: [],
    isDirty,
  })
}

function GuestNetworkNotices({
  network,
  validation,
  isDirty,
}: Readonly<{
  network: GuestNetworkId
  validation: GuestNetworkValidation
  isDirty: boolean
}>): JSX.Element | null {
  const draft = validation.drafts.get(network)
  if (!draft || !isDirty) return null

  const composerFeedbackMessages = getComposerFeedbackMessages(
    validation.whatPreventsSaving,
    draft.id,
  )
  const imageLimitMessage = getImageLimitNoticeMessage(
    draft,
    composerFeedbackMessages,
  )
  if (composerFeedbackMessages.length === 0 && imageLimitMessage === null) {
    return null
  }

  return (
    <EditorFeedbackNotices
      draftId={draft.id}
      composerFeedbackMessages={composerFeedbackMessages}
      imageLimitMessage={imageLimitMessage}
    />
  )
}

type GuestNetworkControlProps = Readonly<{
  network: GuestNetworkId
  onRequestSignIn: (trigger: HTMLElement) => void
}>

function GuestPostTypeBar({
  network,
  onRequestSignIn,
}: GuestNetworkControlProps): JSX.Element | null {
  const postTypes = getGuestNetworkSettings(network)?.postTypes
  if (!postTypes?.length) return null

  return (
    <PostTypeBarView
      name={`guest-${network}-postType`}
      inputIdPrefix={`guest-${network}-`}
      postTypes={[...postTypes]}
      value={postTypes[0].value}
      onChange={(_value, input): void => onRequestSignIn(input)}
    />
  )
}

function GuestChannelFields({
  network,
  text,
  onRequestSignIn,
}: GuestNetworkControlProps & Readonly<{ text: string }>): JSX.Element | null {
  if (network === 'youtube') {
    return (
      <ChannelFields className={styles.fields}>
        <GuestYoutubeFields text={text} onRequestSignIn={onRequestSignIn} />
      </ChannelFields>
    )
  }

  const fields = getGuestNetworkSettings(network)?.fields
  if (!fields?.length) return null

  return (
    <ChannelFields className={styles.fields}>
      {fields.map((field) =>
        field.kind === 'chips' ? (
          <StickerChipsField
            key={field.label}
            stickers={field.options}
            value={[]}
            onChange={(): void => requestFromActiveElement()}
            indicator={<LockIcon size="small" />}
          />
        ) : (
          <ChannelFields.Field key={field.label}>
            {field.kind === 'text' && field.Label ? (
              <field.Label />
            ) : (
              <ChannelFields.Label>{field.label}</ChannelFields.Label>
            )}
            <GuestLockedControl onRequestSignIn={onRequestSignIn}>
              {field.placeholder}
            </GuestLockedControl>
          </ChannelFields.Field>
        ),
      )}
    </ChannelFields>
  )

  function requestFromActiveElement(): void {
    if (document.activeElement instanceof HTMLElement) {
      onRequestSignIn(document.activeElement)
    }
  }
}

function GuestLockedControl({
  onRequestSignIn,
  children,
}: Readonly<{
  onRequestSignIn: (trigger: HTMLElement) => void
  children: ReactNode
}>): JSX.Element {
  return (
    <ChannelFields.LockedControl
      indicator={<LockIcon size="small" />}
      indicatorPlacement="end"
      onClick={(event): void => onRequestSignIn(event.currentTarget)}
    >
      {children}
    </ChannelFields.LockedControl>
  )
}

function GuestYoutubeFields({
  text,
  onRequestSignIn,
}: Readonly<{
  text: string
  onRequestSignIn: (trigger: HTMLElement) => void
}>): JSX.Element {
  const lockedCheckbox = (name: string, checked: boolean): JSX.Element => (
    <ChannelFields.Control asChild>
      <Checkbox
        name={name}
        checked={checked}
        onClick={(event): void => onRequestSignIn(event.currentTarget)}
      />
    </ChannelFields.Control>
  )

  return (
    <YoutubeFieldsView
      title={
        <GuestLockedControl onRequestSignIn={onRequestSignIn}>
          {getYoutubeTitleFromText(text) || 'Enter a title for your video'}
        </GuestLockedControl>
      }
      category={
        <GuestLockedControl onRequestSignIn={onRequestSignIn}>
          Autos & Vehicles
        </GuestLockedControl>
      }
      visibility={
        <GuestLockedControl onRequestSignIn={onRequestSignIn}>
          Public
        </GuestLockedControl>
      }
      license={
        <GuestLockedControl onRequestSignIn={onRequestSignIn}>
          Standard YouTube License
        </GuestLockedControl>
      }
      notifySubscribers={lockedCheckbox('notifySubscribers', true)}
      embeddable={lockedCheckbox('embeddable', true)}
      madeForKids={lockedCheckbox('madeForKids', false)}
    />
  )
}
